import axios from 'axios';
import { getApiErrorMessage } from './errorUtils';
import { PRINT_ACK_TIMEOUT_MS } from '../utils/printAckLoop';
import {
  CancelMissingResultSchema,
  PrintOrderListResponseSchema,
  PrintOrderWithSheetsSchema,
  PrintSheetResultSchema,
  type ApiMessageResult,
  type CancelMissingResult,
  type PrintOrderListResponse,
  type PrintOrderWithSheetsResponse,
} from '../schemas/apiResponse.schemas';
import { validateResponse } from './validateResponse';
import type { CreatePrintOrderInput, PrintFailureCode } from '@/shared/types/printOrder.types';
import type { DestinationPrinterId } from '@/shared/types/printer.types';

const BASE_URL = '/api/print-orders';

/** Create a print order and its queue. Reserves storage IDs; prints nothing. */
export const createPrintOrder = async (
  input: CreatePrintOrderInput,
): Promise<PrintOrderWithSheetsResponse> => {
  const response = await axios.post(BASE_URL, input);
  return validateResponse(PrintOrderWithSheetsSchema, response.data, 'POST /api/print-orders');
};

/**
 * Print every remaining queued sheet of an order as one relay job.
 * Shaped as an {@link ApiMessageResult} so it can be driven by `awaitPrintedAck`.
 */
export const printOrder = async (
  orderId: string,
  printerId: DestinationPrinterId,
): Promise<ApiMessageResult> => {
  try {
    const response = await axios.post(
      `${BASE_URL}/${orderId}/print`,
      { printerId },
      { timeout: PRINT_ACK_TIMEOUT_MS },
    );
    const result = validateResponse(
      PrintSheetResultSchema,
      response.data,
      'POST /api/print-orders/:id/print',
    );
    return {
      success: result.success,
      message: result.message || 'Order printed',
      printed: result.printed !== false,
    };
  } catch (error: unknown) {
    console.error('Error printing order:', error);
    return { success: false, message: getApiErrorMessage(error, 'Failed to print order') };
  }
};

/** Mark an order failed from the client (ACK retries exhausted, abandoned, cancelled). */
export const failPrintOrder = async (
  orderId: string,
  failureCode: PrintFailureCode,
  failureReason: string,
  sheetSeq?: number,
): Promise<void> => {
  try {
    await axios.post(`${BASE_URL}/${orderId}/fail`, {
      failureCode,
      failureReason: failureReason.slice(0, 500),
      ...(sheetSeq != null ? { sheetSeq } : {}),
    });
  } catch (error: unknown) {
    // Best effort — the order stays pending and can still be retried from the list.
    console.error('Error failing print order:', error);
  }
};

/** Newest-first page of warehouse print orders. */
export const listPrintOrders = async (
  offset: number = 0,
  limit: number = 20,
): Promise<PrintOrderListResponse> => {
  try {
    const response = await axios.get(BASE_URL, { params: { offset, limit } });
    return validateResponse(PrintOrderListResponseSchema, response.data, 'GET /api/print-orders');
  } catch (error: unknown) {
    console.error('Error listing print orders:', error);
    return { entries: [], hasMore: false };
  }
};

/** Full order plus its queue items. */
export const getPrintOrder = async (orderId: string): Promise<PrintOrderWithSheetsResponse | null> => {
  try {
    const response = await axios.get(`${BASE_URL}/${orderId}`);
    return validateResponse(PrintOrderWithSheetsSchema, response.data, 'GET /api/print-orders/:id');
  } catch (error: unknown) {
    console.error('Error loading print order:', error);
    return null;
  }
};

/** Delete shelf_indexes rows for unique IDs after the scanned prefix. */
export const cancelPrintOrderMissing = async (
  orderId: string,
  scannedPrefix: string,
): Promise<CancelMissingResult> => {
  const response = await axios.post(`${BASE_URL}/${orderId}/cancel-missing`, { scannedPrefix });
  return validateResponse(
    CancelMissingResultSchema,
    response.data,
    'POST /api/print-orders/:id/cancel-missing',
  );
};

/** Delete shelf_indexes rows for every unique ID on the order. */
export const cancelPrintOrderAll = async (orderId: string): Promise<CancelMissingResult> => {
  const response = await axios.post(`${BASE_URL}/${orderId}/cancel-all`, {});
  return validateResponse(
    CancelMissingResultSchema,
    response.data,
    'POST /api/print-orders/:id/cancel-all',
  );
};

/** New order over the unprinted balance, reusing the storage IDs that never printed. */
export const retryPrintOrder = async (orderId: string): Promise<PrintOrderWithSheetsResponse> => {
  const response = await axios.post(`${BASE_URL}/${orderId}/retry`, {});
  return validateResponse(PrintOrderWithSheetsSchema, response.data, 'POST /api/print-orders/:id/retry');
};
